import { parsePortablePrompt, promptContentSchema, promptSlotIdSchema, type PromptContent, type PromptSegment, type ReferenceKind } from "@seedance/contracts";
export interface PromptPoint { segmentIndex: number; offset: number }
export interface PromptRange { start: PromptPoint; end: PromptPoint }
export const PROMPT_CLIPBOARD_TYPE = "application/x-seedance-prompt+json";
export const promptSegmentLength = (segment: PromptSegment): number => segment.type === "text" ? segment.text.length : 1;
export const promptLength = (content: PromptContent): number => content.segments.reduce((sum, segment) => sum + promptSegmentLength(segment), 0);

export function promptPointToOffset(content: PromptContent, point: PromptPoint): number {
  const { segmentIndex, offset } = point;
  if (!Number.isInteger(segmentIndex) || !Number.isInteger(offset) || segmentIndex < 0 || offset < 0) throw new Error("提示词选区无效。");
  if (!content.segments.length && segmentIndex === 0 && offset === 0) return 0;
  const segment = content.segments[segmentIndex];
  if (!segment || offset > promptSegmentLength(segment)) throw new Error("提示词选区无效。");
  return content.segments.slice(0, segmentIndex).reduce((sum, item) => sum + promptSegmentLength(item), offset);
}

export function promptOffsetToPoint(content: PromptContent, offset: number): PromptPoint {
  if (!Number.isInteger(offset) || offset < 0 || offset > promptLength(content)) throw new Error("提示词选区无效。");
  for (let segmentIndex = 0; segmentIndex < content.segments.length; segmentIndex++) {
    const length = promptSegmentLength(content.segments[segmentIndex]!);
    if (offset <= length) return { segmentIndex, offset };
    offset -= length;
  }
  return { segmentIndex: 0, offset: 0 };
}

export function slicePrompt(content: PromptContent, start: number, end: number): PromptSegment[] {
  let position = 0;
  const result: PromptSegment[] = [];
  for (const segment of content.segments) {
    const length = promptSegmentLength(segment);
    const from = Math.max(0, start - position), to = Math.min(length, end - position);
    if (from < to) result.push(segment.type === "text" ? { type: "text", text: segment.text.slice(from, to) } : { ...segment });
    position += length;
  }
  return result;
}

export function replacePromptRange(content: PromptContent, range: PromptRange, inserted: PromptSegment[]): PromptContent {
  const start = promptPointToOffset(content, range.start), end = promptPointToOffset(content, range.end);
  if (end < start) throw new Error("提示词选区顺序无效。");
  const segments: PromptSegment[] = [];
  for (const segment of [...slicePrompt(content, 0, start), ...inserted, ...slicePrompt(content, end, promptLength(content))]) {
    if (segment.type === "text" && !segment.text) continue;
    const previous = segments.at(-1);
    if (segment.type === "text" && previous?.type === "text") previous.text += segment.text;
    else segments.push({ ...segment });
  }
  return promptContentSchema.parse({ segments });
}

/** All imported slots are newly allocated; no ordinal is a binding or media lookup. */
export function parseExternalPromptText(text: string, nextSlotId: () => string): PromptContent {
  const used = new Set<string>();
  const allocate = () => {
    const id = promptSlotIdSchema.parse(nextSlotId());
    if (used.has(id)) throw new Error("新引用槽位 ID 必须唯一。");
    used.add(id); return id;
  };
  const portable = parsePortablePrompt(text, allocate);
  const segments: PromptSegment[] = [];
  const kinds: Record<string, ReferenceKind> = { 图片: "image", 视频: "video", 音频: "audio" };
  for (const segment of portable.segments) {
    if (segment.type === "reference") { segments.push(segment); continue; }
    let cursor = 0;
    // Portable parsing has already classified these as raw/escaped literals. Preserve
    // every character inside them, including anything resembling an ordinal.
    const protectedRanges = Array.from(segment.text.matchAll(/\{\{[\s\S]*?(?:\}\}|$)/gu), match => [match.index, match.index + match[0].length] as const);
    for (const match of segment.text.matchAll(/@(图片|视频|音频)([1-9]\d*)/gu)) {
      if (protectedRanges.some(([start, end]) => match.index >= start && match.index < end)) continue;
      if (match.index > cursor) segments.push({ type: "text", text: segment.text.slice(cursor, match.index) });
      const label = match[0].slice(1);
      // Overlong input is retained verbatim, never silently truncated.
      if (label.length > 255) segments.push({ type: "text", text: match[0] });
      segments.push({ type: "reference", slotId: allocate(), kind: kinds[match[1]!]!, label: label.length > 255 ? `${match[1]}参考待确认` : label });
      cursor = match.index + match[0].length;
    }
    if (cursor < segment.text.length) segments.push({ type: "text", text: segment.text.slice(cursor) });
  }
  return promptContentSchema.parse({ segments });
}

export function parsePromptClipboard(internal: string, text: string, nextSlotId: () => string): { content: PromptContent; error?: string } {
  if (internal) {
    try {
      const original = promptContentSchema.parse(JSON.parse(internal));
      const slots = new Map<string, string>();
      const used = new Set<string>();
      const segments = original.segments.map(segment => {
        if (segment.type === "text") return segment;
        let slotId = slots.get(segment.slotId);
        if (!slotId) {
          slotId = promptSlotIdSchema.parse(nextSlotId());
          if (used.has(slotId)) throw new Error("重复槽位");
          used.add(slotId); slots.set(segment.slotId, slotId);
        }
        return { ...segment, slotId };
      });
      return { content: promptContentSchema.parse({ segments }) };
    } catch {
      return { content: parseExternalPromptText(text || internal, nextSlotId), error: "剪贴板结构无效，已按外部文本保留，请检查待绑定引用。" };
    }
  }
  return { content: parseExternalPromptText(text, nextSlotId) };
}

export function deletePromptRange(content: PromptContent, range: PromptRange, direction: "backward" | "forward"): PromptContent {
  let start = promptPointToOffset(content, range.start), end = promptPointToOffset(content, range.end);
  if (start === end) {
    const logical = content.segments.map(segment => segment.type === "text" ? segment.text : "\ufffc").join("");
    if (direction === "backward") start -= Array.from(logical.slice(0, start)).at(-1)?.length ?? 0;
    else end += Array.from(logical.slice(end))[0]?.length ?? 0;
  }
  return replacePromptRange(content, { start: promptOffsetToPoint(content, start), end: promptOffsetToPoint(content, end) }, []);
}
